// Per-stage timing aggregator for the hair shade path. Pure (no DOM / THREE /
// ORT deps) so it's unit-testable. Keeps a fixed-size ring buffer per stage and
// reports p50/p95 in ms. Created + driven by HairRenderer only when ?perf=1.

// Stage order matches the shade pipeline: view_enc + spec_mod ONNX, the CPU
// unpack loop, the CPU shade loop, the CPU repack. `total` is the wall-clock of
// the whole _shadeFrame. (The GPU re-upload triggered by needsUpdate happens
// later inside Spark's render and isn't captured here.)
export const HAIR_PERF_STAGES = ['viewEnc', 'specMod', 'unpack', 'shade', 'pack', 'total'];

// Linear-interpolated percentile. `p` in [0,100]. Empty list → 0.
export function percentile(values, p) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 1) return sorted[0];
  const rank = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sorted[lo];
  return sorted[lo] * (hi - rank) + sorted[hi] * (rank - lo);
}

// `stages` defaults to the hair pipeline's list so existing callers are
// unchanged; pass your own to reuse the aggregator for another per-frame path
// (e.g. the ORL deform).
export function createPerfAgg(capacity = 60, stages = HAIR_PERF_STAGES) {
  const buffers = new Map(stages.map((s) => [s, []]));
  let frames = 0;

  return {
    capacity,
    frames: () => frames,

    // Push one shade's per-stage sample. Missing/non-number stages are skipped
    // (so a WebGPU-resident path that drops `unpack` doesn't pollute the ring).
    record(sample) {
      for (const s of stages) {
        const v = sample[s];
        if (typeof v !== 'number' || !Number.isFinite(v)) continue;
        const buf = buffers.get(s);
        buf.push(v);
        if (buf.length > capacity) buf.shift();
      }
      frames += 1;
      return frames;
    },

    // { stage: { p50, p95, n } } for window.__HAIR_PERF__ + tests.
    snapshot() {
      const out = {};
      for (const s of stages) {
        const buf = buffers.get(s);
        if (!buf.length) continue;
        out[s] = { p50: percentile(buf, 50), p95: percentile(buf, 95), n: buf.length };
      }
      return out;
    },

    // One-line "viewEnc=p50/p95 …" summary for the console.
    summary() {
      const parts = [];
      for (const s of stages) {
        const buf = buffers.get(s);
        if (!buf.length) continue;
        parts.push(`${s}=${percentile(buf, 50).toFixed(1)}/${percentile(buf, 95).toFixed(1)}`);
      }
      return parts.join(' ');
    },
  };
}
